import { API_CONFIG } from '../utils/apiConfig';
import { createApiClient } from '../utils/apiClient';

const DEFAULT_BASE_URL = API_CONFIG.QUESTIONNAIRE_API_URL;

export interface QuestionnaireResponseServiceConfig {
    /**
     * Optional override for the API base url. Falls back to QUESTIONNAIRE_API_URL.
     */
    baseUrl?: string;
    /**
     * Optional access token override.
     * If not provided, the token will be automatically retrieved from session storage.
     */
    accessToken?: string;
}

export type QuestionnaireResponsePayload = Record<string, unknown>;

/**
 * Gets the API client with automatic token handling.
 * If accessToken is provided in config, it will be used.
 * Otherwise, the token is automatically retrieved from session storage.
 */
function getApiClient(config?: QuestionnaireResponseServiceConfig) {
    const baseUrl = config?.baseUrl || DEFAULT_BASE_URL;

    if (!baseUrl) {
        throw new Error('Missing API base URL for QuestionnaireResponse service');
    }

    return createApiClient({
        baseUrl,
        accessToken: config?.accessToken,
    });
}

export async function listMyQuestionnaireResponses<T = unknown>(
    config?: QuestionnaireResponseServiceConfig,
): Promise<T> {
    const client = getApiClient(config);
    return client.get<T>('/Patient/Me/QuestionnaireResponse');
}

export async function getQuestionnaireResponseById<T = unknown>(
    id: string,
    config?: QuestionnaireResponseServiceConfig,
): Promise<T> {
    if (!id) {
        throw new Error('QuestionnaireResponse id is required');
    }

    const client = getApiClient(config);
    return client.get<T>(`/QuestionnaireResponse/${id}`);
}

export async function createQuestionnaireResponse<T = unknown>(
    payload: QuestionnaireResponsePayload,
    config?: QuestionnaireResponseServiceConfig,
): Promise<T> {
    if (!payload) {
        throw new Error('QuestionnaireResponse payload is required');
    }

    const client = getApiClient(config);
    return client.post<T>('/QuestionnaireResponse', payload);
}
